import { useMoney } from '../hooks/useMoney';
import React, { useMemo, useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import { BudgetCategory } from '../types/ui/Budget';
import '../styles/components/BudgetChart.css';

interface BudgetChartProps {
  categories: BudgetCategory[];
  formatCurrency: (n: number) => string;
}

const PALETTE = [
  '#c19660', '#8b5a3c', '#d4a574', '#5b7a9c', '#7a9c5b',
  '#9c5b7a', '#5b9c8a', '#a09080', '#6b5b4b', '#9c8a5b',
  '#5b6b9c', '#9c5b5b', '#7a5b9c', '#5b9c6b', '#9c7a5b',
  '#5b8a9c', '#9c9c5b', '#8a5b9c',
];

const SHORT_LABELS: Record<string, string> = {
  'Bridal Party': 'Bridal Pty',
  'Bride Specific': 'Bride',
  'Drinks / Beverages': 'Drinks',
  'Content Creation': 'Content',
  'Photography': 'Photo',
  'Videography': 'Video',
  'Entertainment': 'Entertain.',
  'Miscellaneous': 'Misc',
  'Decoration': 'Decor',
  'Accommodation': 'Accom.',
};


const CustomTooltip = ({ active, payload, label, formatCurrency: fc }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bc-tooltip">
      <p className="bc-tooltip-label">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ /* ds-dynamic: series color from chart payload */ color: p.color }}>
          {p.name}: {fc(p.value)}
        </p>
      ))}
    </div>
  );
};

// Matches BudgetChart.css's stacking breakpoint — below it the donut's own
// legend has to move under the pie instead of beside it, or a fixed cx="35%"
// (sized for the wide side-by-side layout) pushes the circle off the left
// edge of a narrow single-column container.
const MOBILE_BREAKPOINT = 860;

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const BudgetChart: React.FC<BudgetChartProps> = ({ categories, formatCurrency }) => {
  const isMobile = useIsMobile();
  const money = useMoney();
  // Axis ticks stay plain numbers while the project has no currency.
  const fmt = (n: number): string => (money.isSet ? money.formatCompact(n) : String(n));
  const active = useMemo(
    () => categories.filter(c => !c.archived && c.planned_amount > 0),
    [categories]
  );

  const barData = useMemo(
    () =>
      active
        .map(c => ({
          name: SHORT_LABELS[c.name] ?? (c.name.length > 12 ? c.name.slice(0, 11) + '…' : c.name),
          fullName: c.name,
          Planned: c.planned_amount,
          Actual: c.actual_amount,
        }))
        .sort((a, b) => b.Planned - a.Planned),
    [active]
  );

  const pieData = useMemo(
    () =>
      active
        .map(c => ({ name: c.name, value: Number(c.planned_amount) || 0 }))
        .filter(c => c.value > 0)
        .sort((a, b) => b.value - a.value),
    [active]
  );

  if (active.length === 0) return null;

  return (
    <div className="bc-root">
      {/* ── Planned vs Actual bar chart ── */}
      <section className="side-section">
        <h4>Planned vs Actual by Category</h4>
        <ResponsiveContainer width="100%" height={isMobile ? 360 : 320}>
          <BarChart data={barData} layout="vertical" margin={{ top: 8, right: 24, bottom: 0, left: isMobile ? 0 : 8 }}>
            <XAxis type="number" tickFormatter={fmt} tick={{ fontSize: isMobile ? 13 : 11 }} />
            <YAxis type="category" dataKey="name" width={isMobile ? 76 : 90} tick={{ fontSize: isMobile ? 13 : 11 }} />
            <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
            <Legend wrapperStyle={{ fontSize: isMobile ? 13 : 12 }} />
            <Bar dataKey="Planned" fill="#d4a574" radius={[0, 3, 3, 0]} maxBarSize={14} />
            <Bar dataKey="Actual" fill="#8b5a3c" radius={[0, 3, 3, 0]} maxBarSize={14} />
          </BarChart>
        </ResponsiveContainer>
      </section>

      {/* ── Spending breakdown donut ── */}
      <section className="side-section">
        <h4>Planned Spend Breakdown</h4>
        <ResponsiveContainer width="100%" height={isMobile ? 440 : 320}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx={isMobile ? '50%' : '35%'}
              cy={isMobile ? '38%' : '50%'}
              innerRadius={isMobile ? 60 : 70}
              outerRadius={isMobile ? 95 : 110}
              paddingAngle={2}
              label={({ percent }: { percent?: number }) =>
                (percent ?? 0) > 0.04 ? `${((percent ?? 0) * 100).toFixed(0)}%` : ''
              }
              labelLine={false}
            >
              {pieData.map((_, i) => (
                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v: unknown) => formatCurrency(Number(v) || 0)} />
            {isMobile ? (
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
                iconSize={10}
                wrapperStyle={{ fontSize: 13, overflowY: 'auto', maxHeight: 140 }}
              />
            ) : (
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                iconSize={10}
                wrapperStyle={{ fontSize: 11, maxWidth: 160, overflowY: 'auto', maxHeight: 280 }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </section>
    </div>
  );
};

export default BudgetChart;
